import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import {
  ACTION_POPUP_CONTENT_INNER_CLASS,
} from '../../../components/popup/popupLayoutSpec';
import Heading from '../../../components/common/Typography/Heading';
import { disableScroll, enableScroll } from '../../../utils/scrollUtils';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import {
  AdminPageTitle,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminSupportingText,
  AdminHeaderAction,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import { slugifyName } from '../../../utils/slugify';
import { resolveTradeCategoryIconSrc } from '../../../data/tradeCategoryIconMap';
import { buildSampleTradeCategories } from './tradeCategoriesDummyData';
import {
  EMPTY_TABLE_MESSAGE,
  LIST_PREVIEW_COUNT,
  TRADE_CATEGORY_ROUTE,
  formatTradeCategoryLastUpdated,
  formatTradeCategoryTableDate,
  getCompletedTradeSubcategories,
  getPublicFacingIconFileNames,
  getPublicFacingNames,
  getPublicFacingSubcategories,
  getPublicSubcategorySourceNames,
  getTradeSubcategoryNames,
  resolvePublicFacingName,
  sortTradeCategoriesAlphabetically,
  trimName,
} from '../../../data/tradeCategoryWorkflow';

const CategoryIcon = ({ src, fileName, className = '', style }) => {
  const imageSrc = resolveTradeCategoryIconSrc(src, fileName);
  return (
    <div
      className={`flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-white border border-lightgray overflow-hidden ${className}`.trim()}
      style={style}
      title={fileName || 'Category icon'}
      aria-hidden
    >
      {imageSrc ? (
        <img src={imageSrc} alt="" className="w-6 h-6 object-contain" />
      ) : (
        <i className="fi fi-rs-picture text-base text-primaryblue" aria-hidden />
      )}
    </div>
  );
};

const PREVIEW_TABLE_COLS = 'auto auto';

const subcategoryPreviewRows = (category) =>
  getCompletedTradeSubcategories(category)
    .slice()
    .sort((a, b) => trimName(a.name).localeCompare(trimName(b.name)))
    .map((sub) => ({ id: sub.id || '—', name: trimName(sub.name) }));

const publicFacingPreviewRows = (category) =>
  getPublicFacingSubcategories(category).map((sub) => ({
    id: sub.id || '—',
    name: resolvePublicFacingName(sub),
  }));

const publicSubcategoryPreviewRows = (category) =>
  getPublicFacingSubcategories(category).map((sub) => ({
    id: sub.id || '—',
    name: trimName(sub.name),
  }));

const PreviewNamesModal = ({ title, idLabel, nameLabel, rows, onClose }) => {
  useEffect(() => {
    disableScroll();
    return () => enableScroll();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/20 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="list-preview-modal-title"
    >
      <div className="flex-shrink-0 flex items-center justify-end h-[60px] sm:h-[80px] pr-4 sm:pr-6 z-10">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center text-offblack"
          aria-label="Close"
        >
          <i className="fi fi-rs-cross text-sm cursor-pointer" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-white rounded-t-xl min-h-0">
        <div className="grid min-h-full place-items-center py-10 px-4">
          <div className="mx-auto max-w-[calc(100vw-2rem)] hidden-scrollbar lg:pb-0 relative">
            <div id="list-preview-modal-title">
              <Heading
                variant="h6"
                className="flex items-center justify-center text-center text-offblack"
              >
                {title}
              </Heading>
            </div>
            <div className={`${ACTION_POPUP_CONTENT_INNER_CLASS} !w-max max-w-full mx-auto`}>
              <div
                className="grid text-offblack"
                style={{ gridTemplateColumns: PREVIEW_TABLE_COLS }}
              >
                <div className="flex items-center whitespace-nowrap py-3 pr-12 border-b border-lightgray">
                  <AdminTableHeader title={idLabel}>{idLabel}</AdminTableHeader>
                </div>
                <div className="flex items-center whitespace-nowrap py-3 border-b border-lightgray">
                  <AdminTableHeader title={nameLabel}>{nameLabel}</AdminTableHeader>
                </div>
                {rows.map((row, index) => {
                  const isLast = index === rows.length - 1;
                  const cellBorder = isLast ? '' : 'border-b border-lightgray';
                  return (
                    <React.Fragment key={`${row.id}-${row.name}-${index}`}>
                      <div className={`flex items-center whitespace-nowrap py-3 pr-12 ${cellBorder}`}>
                        <AdminTableMeta title={row.id}>{row.id}</AdminTableMeta>
                      </div>
                      <div className={`flex items-center min-w-0 py-3 ${cellBorder}`}>
                        <AdminTableBody className="break-words block" title={row.name}>
                          {row.name}
                        </AdminTableBody>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviewListCell = ({ items, emptyLabel = '—', onViewAll }) => {
  if (!items || items.length === 0) {
    return (
      <AdminTableBody className="truncate block" title={emptyLabel}>
        {emptyLabel}
      </AdminTableBody>
    );
  }

  const preview = items.slice(0, LIST_PREVIEW_COUNT).join(', ');
  const extraCount = items.length - LIST_PREVIEW_COUNT;
  const fullText = items.join(', ');

  return (
    <AdminTableBody className="block min-w-0 w-full" title={fullText}>
      <span className="flex items-center min-w-0 w-full gap-1">
        <span className="min-w-0 truncate">{preview}</span>
        {extraCount > 0 ? (
          <button
            type="button"
            className="text-primaryblue hover:underline shrink-0 whitespace-nowrap"
            onClick={(e) => {
              e.stopPropagation();
              onViewAll?.();
            }}
          >
            +{extraCount} more
          </button>
        ) : null}
      </span>
    </AdminTableBody>
  );
};

const PublicFacingIconsStack = ({ category }) => {
  const icons = getPublicFacingSubcategories(category).filter((sub) =>
    Boolean(resolveTradeCategoryIconSrc(sub.publicFacingIconUrl, sub.publicFacingIconFileName)),
  );
  if (!icons.length) {
    return (
      <AdminTableBody className="break-words block" title="—">
        —
      </AdminTableBody>
    );
  }

  const fileNames = getPublicFacingIconFileNames(category);
  return (
    <div className="flex items-center -space-x-2" title={fileNames.join(', ')}>
      {icons.map((sub, index) => (
        <CategoryIcon
          key={sub.id || sub.localKey || index}
          src={sub.publicFacingIconUrl}
          fileName={sub.publicFacingIconFileName}
          className="relative bg-white ring-1 ring-white"
          style={{ zIndex: icons.length - index }}
        />
      ))}
    </div>
  );
};

const tableCols =
  'minmax(92px, 0.7fr) minmax(72px, auto) minmax(140px, 1.2fr) minmax(180px, 1.4fr) minmax(120px, 1fr) minmax(160px, 1.3fr) minmax(160px, 1.3fr) minmax(150px, 1.2fr) minmax(150px, 1.2fr) minmax(56px, auto)';

const TradeCategories = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState(() => buildSampleTradeCategories());
  const [previewModal, setPreviewModal] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState('');

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.SINGLE);

  useEffect(() => {
    const incoming = location.state?.tradeCategories;
    const flash = location.state?.flashMessage;
    if (Array.isArray(incoming)) {
      setCategories(incoming);
    }
    if (flash) {
      setSaveSuccess(flash);
    }
    if (incoming || flash) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const filteredCategories = useMemo(
    () => sortTradeCategoriesAlphabetically(categories),
    [categories],
  );

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRows,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(filteredCategories);

  const categoryPath = (row) =>
    `${TRADE_CATEGORY_ROUTE}/${encodeURIComponent(slugifyName(row.name))}`;

  const openCategoryEdit = (row) => {
    navigate(`${categoryPath(row)}/edit`, {
      state: { category: row, tradeCategories: categories },
    });
  };

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <AdminPageTitle className="!mb-0">Trade Categories</AdminPageTitle>
          <div className="flex flex-wrap items-center gap-3">
            <AdminHeaderAction
              size="lg"
              onClick={() =>
                navigate(`${TRADE_CATEGORY_ROUTE}/add`, { state: { tradeCategories: categories } })
              }
            >
              Add Trade Category
            </AdminHeaderAction>
          </div>
        </div>

        {saveSuccess && (
          <AdminSupportingText className="text-freshgreen mb-4">{saveSuccess}</AdminSupportingText>
        )}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            {filteredCategories.length === 0 ? (
              <div className="py-10 px-4 text-center">
                <AdminSupportingText className="text-mediumgray">
                  {EMPTY_TABLE_MESSAGE}
                </AdminSupportingText>
              </div>
            ) : (
              <>
                <div
                  className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-4 gap-y-2 min-w-[1100px]"
                  style={{ gridTemplateColumns: tableCols }}
                >
                  <div className="flex items-center">
                    <AdminTableHeader title="Trade ID">Trade ID</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader title="Icon">Icon</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader title="Trade Category">Trade Category</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader title="Subcategories">Subcategories</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader title="Public Icons">Public Icons</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader title="Public-Facing">Public-Facing</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader title="Public Subcategories">
                      Public Subcategories
                    </AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader title="Created On">Created On</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader title="Last Updated On">Last Updated On</AdminTableHeader>
                  </div>
                  <div className="flex items-center justify-center">
                    <AdminTableHeader>Actions</AdminTableHeader>
                  </div>
                </div>

                {paginatedRows.map((row) => {
                  const subcategoryLabels = getTradeSubcategoryNames(row);
                  const publicFacingNames = getPublicFacingNames(row);
                  const publicSubcategoryNames = getPublicSubcategorySourceNames(row);
                  const lastUpdated = formatTradeCategoryLastUpdated(row);

                  return (
                    <div
                      key={row.id}
                      className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-4 gap-y-2 min-w-[1100px]"
                      style={{ gridTemplateColumns: tableCols }}
                    >
                      <div className="flex items-center">
                        <AdminTableBody className="whitespace-nowrap">{row.id}</AdminTableBody>
                      </div>
                      <div className="flex items-center">
                        <CategoryIcon src={row.iconUrl} fileName={row.iconFileName} />
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="break-words whitespace-normal" title={row.name}>
                          {row.name}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <PreviewListCell
                          items={subcategoryLabels}
                          onViewAll={() =>
                            setPreviewModal({
                              title: 'All Subcategories',
                              idLabel: 'Subcategory ID',
                              nameLabel: 'Subcategory',
                              rows: subcategoryPreviewRows(row),
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center">
                        <PublicFacingIconsStack category={row} />
                      </div>
                      <div className="flex items-center min-w-0">
                        <PreviewListCell
                          items={publicFacingNames}
                          onViewAll={() =>
                            setPreviewModal({
                              title: 'All Public-Facing',
                              idLabel: 'Subcategory ID',
                              nameLabel: 'Public-Facing',
                              rows: publicFacingPreviewRows(row),
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center min-w-0">
                        <PreviewListCell
                          items={publicSubcategoryNames}
                          onViewAll={() =>
                            setPreviewModal({
                              title: 'All Public Subcategories',
                              idLabel: 'Subcategory ID',
                              nameLabel: 'Subcategory',
                              rows: publicSubcategoryPreviewRows(row),
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center">
                        <AdminTableMeta className="whitespace-nowrap">
                          {formatTradeCategoryTableDate(row.createdOn)}
                        </AdminTableMeta>
                      </div>
                      <div className="flex items-center">
                        <AdminTableMeta className="whitespace-nowrap">{lastUpdated}</AdminTableMeta>
                      </div>
                      <div
                        className="flex items-center justify-center relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[row.id] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowActions(row.id);
                            }}
                            aria-expanded={!!rowActionsOpen[row.id]}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[row.id] && rowActionsMenuLayout ? (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeRowActions();
                                  openCategoryEdit(row);
                                }}
                              >
                                Edit
                              </AdminRowMenuItem>
                            </AdminFixedRowActionsMenu>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />

        {previewModal ? (
          <PreviewNamesModal
            title={previewModal.title}
            idLabel={previewModal.idLabel}
            nameLabel={previewModal.nameLabel}
            rows={previewModal.rows}
            onClose={() => setPreviewModal(null)}
          />
        ) : null}
      </div>
    </AdminLayout>
  );
};

export default TradeCategories;
